import { BankAccountType, useWealthOverview } from '@egw/api'
import { ActionTile, Button, ButtonVariant, PageHeaderRedesign } from '@egw/components'
import { DEFAULT_CURRENCY_CODE, WEALTH_ROUTES } from '@egw/constants'
import { sendEvent } from '@egw/events'
import { useTranslation } from '@egw/i18n'
import { Currency, getCurrencySymbol, getLastRefreshedText } from '@egw/utils'
import { InformationCircleIcon } from '@heroicons/react/16/solid'
import { type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'

export const CashAccountsOverviewRedesignPage: FC = () => {
  const { t } = useTranslation('features-account')
  const navigate = useNavigate()

  const { data: wealthOverviewData, isLoading, error, dataUpdatedAt } = useWealthOverview()
  const lastRefreshed = dataUpdatedAt ? getLastRefreshedText({ t, timestamp: dataUpdatedAt }) : undefined

  const isLoadingData = isLoading
  const errorData = error

  const checkingAccounts = wealthOverviewData?.highYieldChecking?.accounts ?? []
  const savingsAccounts = wealthOverviewData?.highYieldSavings?.accounts ?? []
  const allCashAccounts = [...checkingAccounts, ...savingsAccounts]

  // Calculate total cash balance
  const totalCashBalance = allCashAccounts.reduce((total, account) => {
    return total + (account.balance.total.amount || 0)
  }, 0)

  const totalAvailableBalance = allCashAccounts.reduce((total, account) => {
    return total + (account.balance.available.amount || 0)
  }, 0)

  const currencyCode =
    checkingAccounts[0]?.balance.total.currencyCode ??
    savingsAccounts[0]?.balance.total.currencyCode ??
    DEFAULT_CURRENCY_CODE

  return (
    <>
      {isLoadingData ? (
        <div className="p-xs bg-primary-500 typo-head-1 egw-container">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {errorData ? (
        <div className="egw-container">
          <InformationCircleIcon className="w-12 h-12 fill-red-500 mb-4" />
          <p className="text-error text-2xl font-light mb-10">{t('common.generic_error')}</p>
          <p className="text-body text-xl">{t('checking.error')}</p>
        </div>
      ) : null}

      {wealthOverviewData ? (
        <>
          <PageHeaderRedesign
            subtitle={t('overview.cash_accounts').toUpperCase()}
            title={t('overview.evergreen_cash')}
            backLinkUrl={WEALTH_ROUTES.CASH_ACCOUNTS_OVERVIEW.backPath}
            lastRefreshed={lastRefreshed}
          />
          <div className="egw-container">
            <div className="overview-card mb-md">
              <h3 className="typo-primary-sm-light text-text-header">{t('checking.total_balance')}</h3>
              <p className="typo-primary-2xl-regular text-text-header">
                {currencyCode ? <span>{getCurrencySymbol(currencyCode)}</span> : null}
                <span>
                  {totalCashBalance
                    ? Currency.integerCents.onlyMinus(totalCashBalance)
                    : Currency.integerCents.onlyMinus(0)}
                </span>
              </p>
              <div className="flex typo-primary-base-regular text-text-meta">
                <p className="flex-1">{t('checking.total_available_balance')}</p>
                <div>
                  {currencyCode ? <span>{getCurrencySymbol(currencyCode)}</span> : null}
                  <span>
                    {totalAvailableBalance
                      ? Currency.integerCents.onlyMinus(totalAvailableBalance)
                      : Currency.integerCents.onlyMinus(0)}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex gap-sm mb-10">
              <div className="flex-1">
                <Button
                  variant={ButtonVariant.Secondary}
                  size="small"
                  className="w-full py-sm"
                  testId="cash-overview-transfer-button"
                  onClick={() => {
                    sendEvent('user_cashAccountsTransfer_click', {
                      eventSource: 'egWealth',
                      category: 'transfer',
                      subCategory: 'intent',
                    })
                    void navigate(WEALTH_ROUTES.TRANSFER.path)
                  }}
                >
                  {t('account_transfer.transfer')}
                </Button>
              </div>
              <div className="flex-1">
                <Button
                  variant={ButtonVariant.Secondary}
                  size="small"
                  className="w-full py-4"
                  testId="cash-overview-documents-button"
                  onClick={() => void navigate(WEALTH_ROUTES.DOCUMENTS.path)}
                >
                  {t('overview.documents')}
                </Button>
              </div>
            </div>

            {/* Account Links */}
            <div className="mb-2xl flex flex-col gap-sm">
              {allCashAccounts.map((account) => {
                return (
                  <ActionTile
                    key={account.alias}
                    variant="account"
                    title={account.accountName}
                    description={t(`bank_account_titles.${account.accountTitle}`)}
                    value={Currency.integerCents.omitSign(account.balance.total.amount)}
                    onClick={() => {
                      if (account.accountType === BankAccountType.HIGH_YIELD_CHECKING) {
                        void navigate(WEALTH_ROUTES.CHECKING_ACCOUNT.path.replace(':alias', account.alias.toString()))
                      } else if (account.accountType === BankAccountType.HIGH_YIELD_SAVINGS) {
                        void navigate(WEALTH_ROUTES.SAVINGS_ACCOUNT.path.replace(':alias', account.alias.toString()))
                      }
                    }}
                    testId={`cash-accounts-overview-${account.alias.toString()}-link`}
                  />
                )
              })}
            </div>
          </div>
        </>
      ) : null}
    </>
  )
}
